import React, { useContext, useEffect, useState } from "react";
import { displayToastMessage } from "@/react/notifications";
import { TYPE } from "@8x8/oxygen-toaster";
import {
  useCreateLNPMutation,
  useDeleteLNPMutation,
  useUpdateLNPMutation,
} from "@/react/features/globalization/globalizationApi";
import { HorizontalTableContext } from "@/react/features/globalization/HorizontalTable";
import Button from "@8x8/oxygen-button";
import {
  CREATE_TEXT_KO,
  CREATE_TEXT_OK,
  DELETE_TEXT_KO,
  DELETE_TEXT_OK,
  EDITED_TEXT_KO,
  EDITED_TEXT_OK,
  TOAST_DURATION,
} from "@/react/features/globalization/constants";
import { Spinner } from "@8x8/oxygen-loaders";
import { LNPCard } from "@/react/features/globalization/components/cell-renders/LNPCard";
import useCreateNumberType from "@/react/features/globalization/hooks/useCreateNumberType";
import useHasRights from "@/react/features/globalization/hooks/useHasRights";
import styled, { css } from "styled-components";

const MainContainer = styled.div`
  ${({ isColumnDisplay }) =>
    isColumnDisplay &&
    css`
      gap: 8px;
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      margin: 10px;
    `};
`;

const StyledLNPCard = styled(LNPCard)`
  width: 500px;
  ${({ isColumnDisplay }) =>
    isColumnDisplay &&
    css`
      padding-right: 15px;
      padding-left: 15px;
      flex: 1;
    `};
`;

export const LNPDetails = ({ row, isColumnDisplay }) => {
  let [list, setList] = useState([]);
  const tableContext = useContext(HorizontalTableContext);

  const [deleteLNP] = useDeleteLNPMutation();
  const [updateLNPDetails] = useUpdateLNPMutation();
  const [createLnpDetails, createResponse] = useCreateLNPMutation();
  const [onCreateNumberTypeHook] = useCreateNumberType();

  useEffect(() => {
    setList(row.lnpDetails);
  }, [row.lnpDetails]);

  const handleUpdateEntity = (entry, field, value) => {
    const updatedItems = list.map((item) => {
      if (item.id === entry.id) {
        return { ...item, [field]: value };
      }
      return item;
    });
    setList(updatedItems);
  };

  const onUpdate = async (entry, field, value) => {
    const backupList = [...list];
    handleUpdateEntity(entry, field, value);
    const updatedItems = list.map((item) => {
      if (item.id === entry.id) {
        return { ...item, [field]: value };
      }
      return item;
    });
    setList(updatedItems);
    try {
      const restPayload = {};
      restPayload[field] = value;
      await updateLNPDetails({
        id: entry.id,
        payload: restPayload,
      }).unwrap();
      displayToastMessage(EDITED_TEXT_OK, {
        duration: TOAST_DURATION,
        type: TYPE.info,
      });
    } catch (e) {
      setList(backupList);
      displayToastMessage(EDITED_TEXT_KO, { duration: TOAST_DURATION });
    }
  };

  const onDelete = async (targetItem) => {
    const initialList = [...list];
    const updatedItems = list.filter((item) => item.id !== targetItem.id);
    setList(updatedItems);
    try {
      await deleteLNP(targetItem.id).unwrap();
      displayToastMessage(DELETE_TEXT_OK, {
        duration: TOAST_DURATION,
        type: TYPE.info,
      });
    } catch (e) {
      setList(initialList);
      displayToastMessage(DELETE_TEXT_KO, { duration: TOAST_DURATION });
    }
  };

  const appendNewItem = async () => {
    const defaultNumberType = tableContext.meta.numberTypesOptions[0];
    const newOption = {
      international: { id: tableContext.meta.internationalId },
      number_type: { id: defaultNumberType.value },
      status: false,
      required_documents: "Fill in the Required Documents",
    };
    try {
      const response = await createLnpDetails(newOption).unwrap();
      const templist = [...list, { ...response, _created: true }];
      setList(templist);
      displayToastMessage(CREATE_TEXT_OK, {
        duration: TOAST_DURATION,
        type: TYPE.info,
      });
    } catch (e) {
      displayToastMessage(CREATE_TEXT_KO, { duration: TOAST_DURATION });
    }
  };

  const onCreateNumberType = async (row, field, selectedOption) => {
    onCreateNumberTypeHook(row, field, selectedOption, onUpdate);
  };
  const { lnp: canEdit } = useHasRights(tableContext.meta.rights);

  const content = (
    <>
      {list.length === 0 && <>No LNP details</>}
      {list.length > 0 &&
        list.map((lnpDetails) => (
          <StyledLNPCard
            key={lnpDetails.id}
            data={lnpDetails}
            isEdit={tableContext.isEdit && !isColumnDisplay}
            onDelete={onDelete}
            onUpdate={onUpdate}
            onCreateNumberType={onCreateNumberType}
          />
        ))}
      {tableContext.isEdit && canEdit.create && (
        <>
          <div>{createResponse.status === "pending" && <Spinner />}</div>
          <Button
            size="small"
            isDisabled={createResponse.status === "pending"}
            variant="success"
            onClick={appendNewItem}
          >
            Add
          </Button>
        </>
      )}
    </>
  );

  return (
    <MainContainer isColumnDisplay={isColumnDisplay}>{content}</MainContainer>
  );
};

LNPDetails.defaultProps = {
  isColumnDisplay: false,
};
